import React, { useState, useRef } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Upload, 
  Download, 
  FileSpreadsheet, 
  Users, 
  Briefcase,
  TrendingUp,
  Database,
  CheckCircle2,
  AlertCircle,
  Trash2,
  RefreshCw
} from 'lucide-react';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CargarDatosPage = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('ejecutivos');

  // Template structures for download
  const templates = {
    ejecutivos: {
      filename: 'plantilla_ejecutivos.xlsx',
      headers: ['nombre', 'iniciales', 'region', 'email'],
      example: [
        { nombre: 'Juan Pérez', iniciales: 'JP', region: 'Región Metropolitana', email: 'jperez@empresa.com' },
        { nombre: 'María García', iniciales: 'MG', region: 'Región Sur', email: 'mgarcia@empresa.com' },
      ]
    },
    clientes: {
      filename: 'plantilla_clientes.xlsx',
      headers: ['nombre', 'ejecutivo_email', 'ruta', 'industria', 'contacto_nombre', 'contacto_email', 'penta_total', 
                'mes_1', 'mes_2', 'mes_3', 'mes_4', 'mes_5', 'mes_6', 'mes_7', 'mes_8', 'mes_9', 
                'mes_10', 'mes_11', 'mes_12', 'mes_13', 'mes_14', 'mes_15', 'mes_16', 'mes_17', 'mes_18'],
      example: [
        { 
          nombre: 'Frigorífico del Sur', 
          ejecutivo_email: 'jperez@empresa.com', 
          ruta: 'MVD→MIA', 
          industria: 'Frigorífico',
          contacto_nombre: 'Carlos López',
          contacto_email: 'clopez@frigosur.com',
          penta_total: 8000,
          mes_1: 1200, mes_2: 1150, mes_3: 1300, mes_4: 1250, mes_5: 1400, mes_6: 1350,
          mes_7: 1280, mes_8: 1320, mes_9: 1450, mes_10: 1500, mes_11: 1600, mes_12: 1700,
          mes_13: 1400, mes_14: 1350, mes_15: 1500, mes_16: 1550, mes_17: 1650, mes_18: 1750
        },
      ]
    },
    oportunidades: {
      filename: 'plantilla_oportunidades.xlsx',
      headers: ['cliente_nombre', 'kgs_potenciales', 'probabilidad', 'mes_cierre', 'descripcion'],
      example: [
        { 
          cliente_nombre: 'Frigorífico del Sur', 
          kgs_potenciales: 500, 
          probabilidad: 75, 
          mes_cierre: '2025-03',
          descripcion: 'Expansión de servicios a nueva ruta'
        },
      ]
    },
    penta: {
      filename: 'plantilla_penta.xlsx',
      headers: ['cliente_nombre', 'penta_total'],
      example: [
        { cliente_nombre: 'Frigorífico del Sur', penta_total: 8500 },
        { cliente_nombre: 'Laboratorio PharmaCare', penta_total: 5000 },
      ]
    }
  };

  const downloadTemplate = (type) => {
    const template = templates[type];
    const ws = XLSX.utils.json_to_sheet(template.example);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    XLSX.writeFile(wb, template.filename);
    toast.success(`Plantilla ${template.filename} descargada`);
  };

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadResults(null);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        toast.error('El archivo está vacío');
        setUploading(false);
        return;
      }

      // Get token
      const token = localStorage.getItem('token');
      
      // Send to backend
      const response = await fetch(`${BACKEND_URL}/api/import/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ data: jsonData })
      });

      const result = await response.json();

      if (response.ok) {
        setUploadResults({
          success: true,
          message: result.message,
          imported: result.imported,
          errors: result.errors || []
        });
        toast.success(`${result.imported} registros importados correctamente`);
      } else {
        setUploadResults({
          success: false,
          message: result.detail || 'Error al importar',
          errors: result.errors || []
        });
        toast.error(result.detail || 'Error al importar');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al procesar el archivo');
      setUploadResults({
        success: false,
        message: 'Error al procesar el archivo',
        errors: [error.message]
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClearData = async (type) => {
    if (!window.confirm(`¿Estás seguro de que quieres eliminar todos los ${type}? Esta acción no se puede deshacer.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/clear/${type}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (response.ok) {
        toast.success(result.message);
      } else {
        toast.error(result.detail || 'Error al eliminar datos');
      }
    } catch (error) {
      toast.error('Error al eliminar datos');
    }
  };

  const handleResetDemo = async () => {
    if (!window.confirm('¿Restaurar los datos de demostración? Esto eliminará los datos actuales y cargará datos de ejemplo.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/reset-demo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      if (response.ok) {
        toast.success('Datos de demostración restaurados');
      } else {
        toast.error(result.detail || 'Error al restaurar datos');
      }
    } catch (error) {
      toast.error('Error al restaurar datos');
    }
  };

  const tabConfig = [
    { 
      id: 'ejecutivos', 
      label: 'Ejecutivos', 
      icon: Briefcase,
      description: 'Carga la lista de ejecutivos de ventas con su región y email.',
      fields: ['nombre', 'iniciales', 'region', 'email']
    },
    { 
      id: 'clientes', 
      label: 'Clientes', 
      icon: Users,
      description: 'Carga clientes con su historial de 18 meses de volumen (kgs). El ejecutivo_email debe coincidir con un ejecutivo existente.',
      fields: ['nombre', 'ejecutivo_email', 'ruta', 'industria', 'penta_total', 'mes_1...mes_18']
    },
    { 
      id: 'oportunidades', 
      label: 'Oportunidades', 
      icon: TrendingUp,
      description: 'Carga el pipeline de oportunidades. El cliente_nombre debe coincidir con un cliente existente.',
      fields: ['cliente_nombre', 'kgs_potenciales', 'probabilidad', 'mes_cierre', 'descripcion']
    },
    { 
      id: 'penta', 
      label: 'Penta-Transaction', 
      icon: Database,
      description: 'Actualiza el volumen total de mercado por cliente (datos Penta).',
      fields: ['cliente_nombre', 'penta_total']
    },
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="cargar-datos-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Cargar Datos</h1>
            <p className="text-slate-500 mt-1">Importa datos desde archivos Excel para alimentar el sistema</p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleResetDemo}
            className="gap-2"
            data-testid="reset-demo-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Restaurar Demo
          </Button>
        </div>

        {/* Info Card */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-indigo-900">Importación desde Excel</h3>
              <p className="text-sm text-indigo-700 mt-1">
                Descarga la plantilla correspondiente, complétala con tus datos y súbela. 
                El sistema validará los datos y mostrará los errores si los hay.
                <br />
                <strong>Orden recomendado:</strong> 1) Ejecutivos → 2) Clientes → 3) Oportunidades → 4) Penta
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            {tabConfig.map(tab => (
              <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-2">
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {tabConfig.map(tab => (
            <TabsContent key={tab.id} value={tab.id}>
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <tab.icon className="w-5 h-5 text-slate-400" />
                      Importar {tab.label}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">{tab.description}</p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleClearData(tab.id)}
                    className="gap-2"
                    data-testid={`clear-${tab.id}-btn`}
                  >
                    <Trash2 className="w-4 h-4" />
                    Limpiar
                  </Button>
                </div>

                {/* Fields info */}
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <p className="text-xs font-medium text-slate-500 mb-2">Campos requeridos:</p>
                  <div className="flex flex-wrap gap-2">
                    {tab.fields.map(field => (
                      <span key={field} className="text-xs bg-white px-2 py-1 rounded border border-slate-200 font-mono">
                        {field}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => downloadTemplate(tab.id)}
                    className="gap-2"
                    data-testid={`download-template-${tab.id}`}
                  >
                    <Download className="w-4 h-4" />
                    Descargar Plantilla
                  </Button>

                  <div className="relative">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(e) => handleFileUpload(e, tab.id)}
                      className="hidden"
                      id={`file-upload-${tab.id}`}
                      data-testid={`file-input-${tab.id}`}
                    />
                    <Button
                      onClick={() => document.getElementById(`file-upload-${tab.id}`).click()}
                      disabled={uploading}
                      className="gap-2"
                      data-testid={`upload-${tab.id}-btn`}
                    >
                      <Upload className="w-4 h-4" />
                      {uploading ? 'Procesando...' : 'Subir Archivo'}
                    </Button>
                  </div>
                </div>

                {/* Upload Results */}
                {uploadResults && (
                  <div className={`mt-6 p-4 rounded-lg ${
                    uploadResults.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      {uploadResults.success ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
                      )}
                      <div>
                        <p className={`font-medium ${uploadResults.success ? 'text-emerald-800' : 'text-rose-800'}`}>
                          {uploadResults.message}
                        </p>
                        {uploadResults.imported && (
                          <p className="text-sm text-emerald-600 mt-1">
                            {uploadResults.imported} registros importados
                          </p>
                        )}
                        {uploadResults.errors && uploadResults.errors.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm font-medium text-rose-700">Errores:</p>
                            <ul className="text-sm text-rose-600 list-disc list-inside">
                              {uploadResults.errors.slice(0, 5).map((err, i) => (
                                <li key={i}>{err}</li>
                              ))}
                              {uploadResults.errors.length > 5 && (
                                <li>...y {uploadResults.errors.length - 5} errores más</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </Layout>
  );
};

export default CargarDatosPage;
